/**
 * Log levels supported by the logging system
 */
export type LogLevel = 'info' | 'warn' | 'error' | 'debug'

/**
 * Log configuration interface
 */
interface LogConfig {
  /**
   * Whether to enable logs in production
   * @default false
   */
  enableInProduction?: boolean
  /**
   * Whether to include timestamp in logs
   * @default true
   */
  showTimestamp?: boolean
}

/**
 * Default configuration for the logging system
 */
const defaultConfig: LogConfig = {
  enableInProduction: false,
  showTimestamp: true,
}

/**
 * Styles for different log levels
 */
const logStyles = {
  info: 'color: #3498db; font-weight: bold;',
  warn: 'color: #f39c12; font-weight: bold;',
  error: 'color: #e74c3c; font-weight: bold;',
  debug: 'color: #9b59b6; font-weight: bold;',
  timestamp: 'color: #7f8c8d; font-style: italic;',
}

/**
 * Icons for different log levels
 */
const logIcons = {
  info: 'ℹ️',
  warn: '⚠️',
  error: '❌',
  debug: '🔍',
}

/**
 * Creates a formatted timestamp string
 * @returns {string} Formatted timestamp
 */
const getTimestamp = (): string => {
  return new Date().toLocaleString('it-IT', {
    timeZone: 'Europe/Rome',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

/**
 * Checks if logging is allowed based on environment and configuration
 * @param {LogConfig} config - Logging configuration
 * @returns {boolean} Whether logging is allowed
 */
const canLog = (config: LogConfig): boolean => {
  const isDev = process.env.NODE_ENV === 'development'
  return isDev || config.enableInProduction === true
}

/**
 * Creates a log function for the specified level
 * @param {LogLevel} level - Log level
 * @returns {Function} Log function for the specified level
 */
const createLogFunction = (level: LogLevel) => {
  return (message: unknown, ...args: unknown[]) => {
    const config = defaultConfig

    if (!canLog(config)) return

    const consoleMethod = level === 'debug' ? 'log' : level

    let logParts = [`%c[${logIcons[level]} ${level.toUpperCase()}]`]
    const styleArgs = [logStyles[level]]

    // Add the message and any additional arguments
    logParts.push(
      typeof message === 'string' ? message : JSON.stringify(message)
    )
    if (args.length > 0) {
      logParts = [
        ...logParts,
        ...args.map((arg) =>
          typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
        ),
      ]
    }

    // Add timestamp if enabled
    if (config.showTimestamp) {
      logParts.push(`%c${getTimestamp()}`)
      styleArgs.push(logStyles.timestamp)
    }

    // Type-safe approach to console methods
    if (consoleMethod === 'info') {
      console.info(logParts.join(' '), ...styleArgs)
    } else if (consoleMethod === 'warn') {
      console.warn(logParts.join(' '), ...styleArgs)
    } else if (consoleMethod === 'error') {
      console.error(logParts.join(' '), ...styleArgs)
    } else {
      console.log(logParts.join(' '), ...styleArgs)
    }
  }
}

/**
 * Log information messages
 * @param {unknown} message - Message to log
 * @param {unknown[]} args - Additional arguments to log
 */
export const infoLog = createLogFunction('info')

/**
 * Log warning messages
 * @param {unknown} message - Message to log
 * @param {unknown[]} args - Additional arguments to log
 */
export const warnLog = createLogFunction('warn')

/**
 * Log error messages
 * @param {unknown} message - Message to log
 * @param {unknown[]} args - Additional arguments to log
 */
export const errorLog = createLogFunction('error')

/**
 * Log debug messages
 * @param {unknown} message - Message to log
 * @param {unknown[]} args - Additional arguments to log
 */
export const debugLog = createLogFunction('debug')

/**
 * Unified logging function
 * @param {LogLevel} level - Log level
 * @param {unknown} message - Message to log
 * @param {unknown[]} args - Additional arguments to log
 */
export const log = (level: LogLevel, message: unknown, ...args: unknown[]) => {
  switch (level) {
    case 'info':
      infoLog(message, ...args)
      break
    case 'warn':
      warnLog(message, ...args)
      break
    case 'error':
      errorLog(message, ...args)
      break
    case 'debug':
      debugLog(message, ...args)
      break
    default:
      infoLog(message, ...args)
  }
}
